import { Link } from 'react-router-dom'

import { isValidComicProjectPublicId } from '../../api/comics'
import type { ComicProject } from '../../lib/comicTypes'
import { formatShelfDate } from '../../lib/dateDisplay'
import { ChromeToolbarDangerButton } from '../ChromeActions'
import { ChromeMetaTag, ChromeMonoTag } from '../ChromeTags'
import { ChromeTooltip } from '../ChromeTooltip'
import { IconTrash } from '../nav-icons'

type Props = {
  project: ComicProject
  angular: boolean
  onDelete?: (project: ComicProject) => void
}

/**
 * 书架单册：3:4 封面 + 元信息区；列宽由栅格列轨约束，单册不铺满整行。
 */
function coverInitial(title: string) {
  const trimmed = title.trim()
  if (!trimmed) return '漫'
  return Array.from(trimmed)[0] ?? '漫'
}

export function ComicBookCard({ project, angular, onDelete }: Props) {
  const updatedLabel = formatShelfDate(project.updatedAt)
  const coverUrl = project.coverUrl?.trim() || undefined
  const hasCover = Boolean(coverUrl)
  const coverMark = coverInitial(project.title)

  const editorTo = isValidComicProjectPublicId(project.id)
    ? `/comics/${encodeURIComponent(project.id)}`
    : '/comics'

  return (
    <article
      className={[
        'group relative flex h-full min-w-0 w-full max-w-full flex-col overflow-hidden border-line bg-panel text-ink shadow-card',
        'transition-[box-shadow] duration-200 motion-reduce:transition-none',
        '[@media(hover:hover)_and_(pointer:fine)]:hover:shadow-float',
        angular ? 'rounded-none border-2' : 'rounded-theme border',
      ].join(' ')}
    >
      <Link
        to={editorTo}
        className={[
          'flex min-h-0 w-full min-w-0 flex-col focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent/50',
        ].join(' ')}
        aria-label={`打开《${project.title}》`}
      >
        <div
          className={[
            'relative w-full shrink-0 overflow-hidden bg-surface-muted',
            'pt-[133.3333%]',
            angular ? '' : 'rounded-t-theme',
          ].join(' ')}
        >
          {hasCover ? (
            <img
              src={coverUrl}
              alt=""
              draggable={false}
              className={[
                'absolute inset-0 block h-full w-full object-cover object-center',
                '[@media(hover:hover)_and_(pointer:fine)]:group-hover:brightness-[1.04]',
              ].join(' ')}
              loading="lazy"
              decoding="async"
            />
          ) : (
            <div
              className={[
                'absolute inset-0 flex flex-col items-center justify-center gap-2.5 px-3 py-4 text-center',
                'bg-[linear-gradient(165deg,color-mix(in_srgb,var(--accent)_14%,var(--surface-muted)),var(--surface-muted))]',
              ].join(' ')}
              aria-hidden
            >
              <div
                className={[
                  'flex h-12 w-12 shrink-0 items-center justify-center border border-line/70 bg-panel/80 font-display text-lg font-semibold text-ink/80 shadow-sm',
                  angular ? 'rounded-none' : 'rounded-full',
                ].join(' ')}
              >
                {coverMark}
              </div>
              <span className="text-[10px] font-medium uppercase tracking-[0.16em] text-ink-soft/75">
                漫画
              </span>
            </div>
          )}
          {hasCover ? (
            <div
              className={[
                'pointer-events-none absolute inset-x-0 bottom-0 h-1/3',
                'bg-gradient-to-t from-[color-mix(in_srgb,var(--panel)_92%,transparent)] to-transparent',
              ].join(' ')}
              aria-hidden
            />
          ) : null}
        </div>
        <div className="flex min-h-[5.25rem] min-w-0 shrink-0 flex-col gap-1 border-t border-line px-3 py-2 sm:px-3.5">
          <h2 className="line-clamp-2 text-sm font-semibold leading-snug text-ink" title={project.title}>
            {project.title}
          </h2>
          {project.synopsis ? (
            <p className="line-clamp-2 text-xs leading-relaxed text-ink-soft">{project.synopsis}</p>
          ) : null}
          <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
            <ChromeMetaTag angular={angular}>{`${project.episodeCount} 话`}</ChromeMetaTag>
            {updatedLabel ? <ChromeMonoTag angular={angular}>{updatedLabel}</ChromeMonoTag> : null}
          </div>
        </div>
      </Link>
      {onDelete ? (
        <div className="pointer-events-none absolute top-2.5 right-2.5 z-[2]">
          <ChromeTooltip content="删除画册" angular={angular} side="top" align="end">
            <ChromeToolbarDangerButton
              type="button"
              className="pointer-events-auto h-8 w-8 !gap-0 !p-0"
              aria-label={`删除《${project.title}》`}
              onClick={() => onDelete(project)}
            >
              <IconTrash className="h-4 w-4" aria-hidden />
            </ChromeToolbarDangerButton>
          </ChromeTooltip>
        </div>
      ) : null}
    </article>
  )
}
